// Skript-Textbausteine: dieselbe deterministische [Token]-Ersetzung, die CallCockpit während des
// Calls anwendet — hierher gezogen, damit die Skript-Bibliothek (ScriptLibrary) exakt dieselbe
// Vorschau zeigen kann wie das, was der Setter später am Ohr liest. Keine Namen/Firmen aus der
// Vorlage sind hartkodiert — nur die Ersetzungslogik + der feste Anbieter-Name.
import type { Lead } from '../types';

// Eigener Firmenname — Vorgabe aus dem Leitkraft-Skript, kein Lead-Feld (jeder Call ist derselbe
// Absender). Änderungen hier wirken sofort auf jeden Block, der [Anbieter] verwendet.
export const ANBIETER_NAME = 'Leitkraft';

export interface ScriptTokenContext {
  vorname: string | null;
  nachname: string | null;
  jobtitel: string | null;
  unternehmen: string | null;
  setter: string | null;
  paid: string | null;
  display: string | null;
  betrag: string | null;
  anbieter: string | null;
  // Rein manuelle Textbausteine ohne automatische Quelle — bleiben ohne Eingabe als sichtbarer
  // Sprech-Hinweis stehen (z. B. "[Anzeigentitel]"), genau wie jeder andere unbekannte Token.
  anzeigentitel?: string | null;
  kollege?: string | null;
  vorgesetzter?: string | null;
  vorgesetzterPosition?: string | null;
  domain?: string | null;
}

export function firstName(full?: string | null): string {
  return (full || '').trim().split(/\s+/)[0] || '';
}

export function lastName(full?: string | null): string {
  const p = (full || '').trim().split(/\s+/);
  return p.length > 1 ? p.slice(1).join(' ') : '';
}

export function pct(x?: number | null): string | null {
  return x == null ? null : String(Math.round(x * 100));
}

// Rückholbaren Betrag konservativ ABRUNDEN auf eine runde Zahl (der Angerufene soll eine greifbare
// Hausnummer hören, keine krumme Zahl): KMU → Tausender, größer → 5er-/10er-Tausender.
export function roundDownEur(eur: number): string {
  const step = eur >= 100000 ? 10000 : eur >= 10000 ? 5000 : 1000;
  const r = Math.max(step, Math.floor(eur / step) * step);
  return r.toLocaleString('de-DE');
}

// Ersetzt [Vorname], [Nachname], [Jobtitel], [Unternehmen], [Setter], [Anbieter], [paid],
// [display], [betrag], [Anzeigentitel], [Kollege], [Vorgesetzter], [Vorgesetzter Position],
// [Domain] (+ Alt-Tokens). Unbekannter/leerer Wert → Token bleibt sichtbar (Cue für den Setter,
// manuell zu ergänzen) — das gilt insbesondere für die rein manuellen Tokens ohne Lead-Quelle.
export function fillPlaceholders(text: string, ctx: ScriptTokenContext): string {
  const map: Record<string, string | null | undefined> = {
    'Vorname': ctx.vorname,
    'Nachname': ctx.nachname,
    'Vorname Nachname': `${ctx.vorname || ''} ${ctx.nachname || ''}`.trim() || null,
    'Jobtitel': ctx.jobtitel,
    'Unternehmen': ctx.unternehmen,
    'Setter': ctx.setter,
    'Mitarbeitervorname': ctx.setter, // Alt-Token-Kompatibilität
    'Anbieter': ctx.anbieter,
    'paid': ctx.paid,
    'display': ctx.display,
    'betrag': ctx.betrag,
    'Anzeigentitel': ctx.anzeigentitel,
    'Kollege': ctx.kollege,
    'Vorgesetzter': ctx.vorgesetzter,
    'Vorgesetzter Position': ctx.vorgesetzterPosition,
    'Domain': ctx.domain,
  };
  return (text || '').replace(/\[([^\]]+)\]/g, (m, key) => {
    const v = map[String(key).trim()];
    return v != null && v !== '' ? v : m;
  });
}

// Baut den Token-Kontext aus Lead + Absender — dieselbe Ableitung, die CallCockpit für den echten
// Call verwendet. `setterName` ist typischerweise der ausgewählte LinkedIn-Account-Name oder,
// als Rückfall, der eingeloggte Nutzername.
export function buildScriptTokenContext(lead: Lead | null | undefined, setterName?: string | null): ScriptTokenContext {
  const sc = lead?.script_context ?? undefined;
  return {
    vorname: firstName(lead?.name),
    nachname: lastName(lead?.name),
    jobtitel: sc?.job_title ?? null,
    unternehmen: lead?.company || sc?.company || null,
    paid: pct(sc?.paid_share),
    display: pct(sc?.display_share),
    setter: firstName(setterName),
    anbieter: ANBIETER_NAME,
    domain: sc?.domain ?? null,
    betrag: sc?.recoverable_eur != null && sc.recoverable_eur > 0
      ? `~${roundDownEur(sc.recoverable_eur)} €/Jahr`
      : 'einen vier- bis fünfstelligen Betrag/Jahr',
  };
}

// Beispielhafter Vorschau-Kontext für die Skript-Bibliothek — kein echter Lead vorhanden, also
// sprechende Platzhalter statt leerer Werte, damit die Vorschau zeigt, WIE ein befüllter Block
// aussieht (bracket-Tokens ohne Beispielwert bleiben bewusst sichtbar, siehe fillPlaceholders).
export function previewScriptTokenContext(): ScriptTokenContext {
  return {
    vorname: 'Max', nachname: 'Mustermann', jobtitel: 'Head of Marketing', unternehmen: 'Muster GmbH',
    setter: 'Alex', anbieter: ANBIETER_NAME, paid: '62', display: '18',
    betrag: '~24.000 €/Jahr', domain: 'muster-gmbh.de',
  };
}
